import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, getAdminToken } from '../api.ts';
import type { Comment, CompareMode, PlaylistItem, Quality, Stroke, Version, VersionStatus } from '../types.ts';
import { useFollowerVideo, useFramePlayer } from '../hooks/useFramePlayer.ts';
import { useFollowSync, useSyncClient } from '../hooks/useSyncClient.ts';
import { useRoomState } from '../hooks/useRoomState.ts';
import { useClipGroups } from '../hooks/useClipGroups.ts';
import { useAnnotationDraft } from '../hooks/useAnnotationDraft.ts';
import { useReviewKeys } from '../hooks/useReviewKeys.ts';
import { parseTimecodeToFrames } from '../timecode.ts';
import { captureVideoFrame } from './AnnotationCanvas.tsx';
import { PlayerStage } from './PlayerStage.tsx';
import { Timeline } from './Timeline.tsx';
import { Scrubber } from './Scrubber.tsx';
import { CommentPanel } from './CommentPanel.tsx';
import { RejectDialog } from './RejectDialog.tsx';
import { ReviewHeader } from './ReviewHeader.tsx';
import { ReviewControls } from './ReviewControls.tsx';
import { DrawToolbar } from './DrawToolbar.tsx';

/** A clip change starts from scratch: no zoom, no pan. */
const NO_TRANSFORM = { zoom: 1, panX: 0, panY: 0 };

/** Shared, so "nothing is drawn here" is the same array every time and not a new prop. */
const NO_STROKES: Stroke[] = [];

/**
 * Rebuild the flat item list so its shots follow `shotIds`, keeping each shot's versions
 * together and in their existing relative order. Shots not named in `shotIds` (should not
 * happen) are appended in their current order so nothing is ever dropped.
 */
function orderItemsByShots(items: PlaylistItem[], shotIds: string[]): PlaylistItem[] {
  const byShot = new Map<string, PlaylistItem[]>();
  for (const it of items) {
    const arr = byShot.get(it.version.shotId);
    if (arr) arr.push(it);
    else byShot.set(it.version.shotId, [it]);
  }
  const out: PlaylistItem[] = [];
  for (const shotId of shotIds) {
    const block = byShot.get(shotId);
    if (block) {
      out.push(...block);
      byShot.delete(shotId);
    }
  }
  for (const block of byShot.values()) out.push(...block);
  return out;
}

/** The shots of an item list in their current order, de-duplicated (one entry per clip). */
function shotOrderOf(items: PlaylistItem[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    if (seen.has(it.version.shotId)) continue;
    seen.add(it.version.shotId);
    out.push(it.version.shotId);
  }
  return out;
}

interface Props {
  sessionId: string;
  displayName: string;
  initialItems: PlaylistItem[];
  playlistId: string;
  playlistName: string;
  /** Whether the playlist has a saved manual order (else clips are shown alphanumerically). */
  manualOrder: boolean;
  /** Whether the project lets reviewers download its clips. */
  canDownload: boolean;
}

export function ReviewPage({
  sessionId,
  displayName,
  initialItems,
  playlistId,
  playlistName,
  manualOrder: initialManualOrder,
  canDownload: canDownloadProp,
}: Props): React.JSX.Element {
  const [items, setItems] = useState<PlaylistItem[]>(initialItems);
  const [comments, setComments] = useState<Comment[]>([]);
  const [quality, setQuality] = useState<Quality>('full');
  const [compareMode, setCompareMode] = useState<CompareMode>('off');
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const [wipe, setWipe] = useState(0.5);
  const [muted, setMuted] = useState(true);
  const [loopMode, setLoopMode] = useState(true);
  const [transform, setTransform] = useState(NO_TRANSFORM);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once anyone drags a clip, the order comes from the items array instead of the shot names.
  const [manualOrder, setManualOrder] = useState(initialManualOrder);
  // Leaves this browser out of the room's playback lock without disconnecting: comments and
  // status still stream, only play/pause/seek/version-jump stop mirroring. A follower gets to
  // scrub the same review privately; a controller who presses this also releases the room.
  const [soloMode, setSoloMode] = useState(false);

  // Layout: the shot strip and the notes column can each be folded away to give the video more
  // room. Shared with the room like zoom and pan — folding one folds it for everyone following —
  // and remembered in this browser as the starting point for the next visit.
  const [stripOpen, setStripOpen] = useState(() => localStorage.getItem('openreview.stripOpen') !== '0');
  const [notesOpen, setNotesOpen] = useState(() => localStorage.getItem('openreview.notesOpen') !== '0');

  // Focus mode (the `f` key): collapse both the shot strip and the notes so the clip fills the
  // screen, and put them back exactly as they were on the way out. Transient — it drives the
  // real panel state so the collapse/expand chevrons keep working, but does not touch the saved
  // preference, so a reload returns to whatever the reviewer had chosen.
  const [focusMode, setFocusMode] = useState(false);
  const panelsBeforeFocus = useRef({ strip: true, notes: true });

  // Only the session host (who holds the admin token) may rearrange the shared playlist.
  const canReorder = Boolean(getAdminToken());
  /*
   * Strictly what this review says — the host is not exempt. The toggle in the reviews list is
   * meant to answer "does this review hand out files", and a host who switched it off should
   * see the review the way the people they sent it to will. Holding the admin token still
   * fetches bytes from the API directly; this is about what the player offers.
   */
  const canDownload = canDownloadProp;

  // The playlist as clips — one per shot, versions switchable in the header.
  const { groups, currentIndex, currentGroup, version, stripItems, select, show, follow, currentIndexRef } =
    useClipGroups(items, !manualOrder);

  const fps = version?.fps ?? 25;
  const frameCount = version?.frameCount ?? 0;
  // Source frame numbering: a clip Nuke stamped as starting on 1001 counts from 1001, not 0.
  // Zero when the file carried no start timecode, so those clips read exactly as before.
  const startFrame = useMemo(() => parseTimecodeToFrames(version?.timecodeStart, fps), [version?.timecodeStart, fps]);

  // Same identity string that PlayerStage puts on the <video key>: switching version or quality
  // mounts a fresh element, and the player hook has to re-attach its listeners to it.
  const player = useFramePlayer(fps, frameCount, `${version?.id ?? 'none'}-${quality}`);
  const draft = useAnnotationDraft(player.frame, version?.id ?? null);
  const compareRef = useRef<HTMLVideoElement | null>(null);
  const sync = useSyncClient(sessionId, displayName);
  const isController = sync.isController;
  // The host has taken the room over and someone else holds it: this browser watches, and its
  // draw / clip-change / play controls stand down until the host releases.
  const lockedOut = sync.locked && !isController;

  const compareVersion = useMemo(
    () => items.find((i) => i.version.id === compareVersionId)?.version ?? null,
    [items, compareVersionId],
  );

  // The clip after this one, handed to the player so it can be decoded while the current one is
  // still running. Not limited to sequence mode: stepping to the next shot by hand benefits from
  // the same head start, and it is one clip either way.
  const nextVersion = useMemo(
    () => stripItems[currentIndex + 1]?.version ?? null,
    [stripItems, currentIndex],
  );

  // Only other versions of the same shot are worth comparing against.
  const compareOptions = useMemo(
    () =>
      items
        .filter((i) => i.version.shotId === version?.shotId && i.version.id !== version?.id)
        .map((i) => i.version),
    [items, version?.shotId, version?.id],
  );

  // Followers get their playback driven by the room; the controller drives it locally; a solo
  // participant is nobody's follower until they rejoin. The returned drift is no longer shown
  // anywhere — the call stays for its correction side effect.
  useFollowSync(player.videoRef, sync, !isController && sync.connected && !soloMode);
  useFollowerVideo(player.videoRef, compareRef, compareMode !== 'off' && compareVersion !== null);

  // Current frame and clip through refs so event handlers registered once still read the live values.
  const frameRef = useRef(player.frame);
  frameRef.current = player.frame;
  const versionIdRef = useRef(version?.id);
  versionIdRef.current = version?.id;

  /*
   * True when somebody else is driving the room. Anything *automatic* must stay quiet then:
   * pushing state promotes the sender to controller, so an unguarded auto-advance would have
   * every follower grab the remote at each clip boundary. Deliberate clicks may take over —
   * that is the point of the auto-handoff — but a clip simply ending must not.
   */
  const followerRef = useRef(false);
  followerRef.current = sync.connected && sync.controllerId !== null && !isController && !soloMode;

  // ------------------------------------------------------ controller -> room

  const pushState = useRoomState(player, sync, {
    itemIndex: currentIndex,
    versionId: version?.id ?? null,
    rate: player.rate,
    compareMode,
    compareVersionId,
    wipe,
    quality,
    loopMode,
    zoom: transform.zoom,
    panX: transform.panX,
    panY: transform.panY,
    // Carry the manual clip order on the heartbeat, so a late joiner lands on the same order.
    clipOrder: manualOrder ? groups.map((g) => g.shotId) : null,
    stripOpen,
    notesOpen,
  });

  const toggleStrip = useCallback(() => {
    const next = !stripOpen;
    setStripOpen(next);
    localStorage.setItem('openreview.stripOpen', next ? '1' : '0');
    pushState({ stripOpen: next });
  }, [stripOpen, pushState]);
  const toggleNotes = useCallback(() => {
    const next = !notesOpen;
    setNotesOpen(next);
    localStorage.setItem('openreview.notesOpen', next ? '1' : '0');
    pushState({ notesOpen: next });
  }, [notesOpen, pushState]);
  // Focus mode folds both panels for the room as well, and unfolds them the same way.
  const toggleFocus = useCallback(() => {
    if (focusMode) {
      const { strip, notes } = panelsBeforeFocus.current;
      setStripOpen(strip);
      setNotesOpen(notes);
      setFocusMode(false);
      pushState({ stripOpen: strip, notesOpen: notes });
    } else {
      panelsBeforeFocus.current = { strip: stripOpen, notes: notesOpen };
      setStripOpen(false);
      setNotesOpen(false);
      setFocusMode(true);
      pushState({ stripOpen: false, notesOpen: false });
    }
  }, [focusMode, stripOpen, notesOpen, pushState]);


  // ------------------------------------------------------------------ loading

  const loadComments = useCallback(async (versionId: string) => {
    const res = await api.listComments(versionId);
    setComments(res.comments);
  }, []);

  useEffect(() => {
    if (!version) return;
    void loadComments(version.id).catch(() => undefined);
  }, [version?.id, loadComments, version]);

  /*
   * Leaving the frame you drew on saves the drawing as a note of its own.
   *
   * A drawing is worth keeping even when nobody typed anything — it marks a frame, and that
   * mark is the point. Saving it as a comment is what puts the tick on the scrubber and the
   * entry in the notes list, both of which jump back to the frame. While you stay on the frame
   * the drawing is still a draft, so typing a note attaches it to that note instead of leaving
   * two entries behind.
   */
  /*
   * The frame behind a pending drawing, captured the instant the pen first lands on it, so the
   * saved note thumbnail shows the drawing over its frame instead of on black. It must be grabbed
   * now, while the <video> still shows that frame: the flush below saves after we have moved off
   * it, when the element is already displaying something else. Cleared with the drawing.
   */
  const drawBackgroundRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (draft.drawFrame === null || draft.drawVersionId === null) {
      drawBackgroundRef.current = null;
      return;
    }
    drawBackgroundRef.current = captureVideoFrame(player.videoRef.current);
    // Only when the pin itself changes — extra strokes on the same frame reuse the snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.drawFrame, draft.drawVersionId]);

  /*
   * Save the pending drawing as a note of its own, against the clip and frame it was drawn on,
   * and empty the pad. There is no save button: this runs whenever the drawing is left behind —
   * moving off its frame, switching clip or version, closing the tab.
   *
   * Clip and version switches used to throw the draft away before this could see it, so a
   * drawing followed by a click on the next clip was silently lost.
   *
   * `lastFlushed` keeps the same drawing from becoming two notes when two paths reach it in one
   * go (a clip switch also moves the frame; StrictMode runs effects twice). It is keyed on the
   * strokes array itself, so a new drawing made while an earlier save is still in flight is not
   * held back the way a single "busy" flag held it back.
   */
  const lastFlushed = useRef<Stroke[] | null>(null);
  const flushDraft = useCallback(
    (opts?: { leavingPage?: boolean }) => {
      const { drawFrame, drawVersionId, strokes } = draft;
      if (strokes.length === 0 || drawFrame === null || drawVersionId === null) return;
      if (lastFlushed.current === strokes) return;
      lastFlushed.current = strokes;

      // The clip it was drawn on, which may no longer be the one on screen.
      const drawnOn = items.find((i) => i.version.id === drawVersionId)?.version;
      // A page on its way out can only send a small body (see api.ts), so it saves the strokes on
      // their own rather than flattened over the frame snapshot.
      const background = opts?.leavingPage ? null : drawBackgroundRef.current;
      const payload = drawnOn ? draft.payloadFor(drawnOn, drawFrame, background) : undefined;
      draft.clear();
      if (!payload) return;

      void api
        .addComment(
          drawVersionId,
          { body: '', frame: drawFrame, author: displayName, annotation: payload },
          { keepalive: opts?.leavingPage },
        )
        .then(() => {
          // Only refresh the notes if that clip is still the one on screen.
          if (drawVersionId === versionIdRef.current) return loadComments(drawVersionId);
          return undefined;
        })
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    },
    [draft, items, displayName, loadComments],
  );
  // For the navigation callbacks, which are kept stable and must not change with every stroke.
  const flushDraftRef = useRef(flushDraft);
  flushDraftRef.current = flushDraft;

  // Moving off the frame (scrubbing, stepping, playing) — or being moved off it by the room.
  useEffect(() => {
    const { drawFrame, drawVersionId, strokes } = draft;
    if (strokes.length === 0 || drawFrame === null || drawVersionId === null) return;
    // Still parked on the frame it belongs to: keep it as a draft, so a note typed now takes it along.
    if (drawVersionId === version?.id && drawFrame === player.frame) return;
    flushDraft();
  }, [player.frame, version?.id, draft, flushDraft]);

  // Closing, reloading or navigating away from the review with a drawing still on the pad.
  useEffect(() => {
    const onPageHide = (): void => flushDraftRef.current({ leavingPage: true });
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // ------------------------------------------------------- remote state -> ui

  useEffect(() => {
    const state = sync.state;
    if (!state || isController || soloMode) return;
    // A manual clip order set by the controller: reorder our items to match. Guarded so the
    // 500 ms heartbeat does not thrash — only reorder when the shot sequence actually differs.
    if (state.clipOrder) {
      const wanted = state.clipOrder;
      setItems((prev) => {
        const current = shotOrderOf(prev);
        const same = current.length === wanted.length && current.every((s, i) => s === wanted[i]);
        return same ? prev : orderItemsByShots(prev, wanted);
      });
      setManualOrder(true);
    }
    // Follow by versionId: it resolves to both the clip and the shown version, so a version
    // switch propagates just like a shot change.
    if (state.versionId) follow(state.versionId);
    setCompareMode(state.compareMode);
    setCompareVersionId(state.compareVersionId);
    setWipe(state.wipe);
    // Loop vs. sequence is part of the room: otherwise a follower left in loop mode would sit
    // on one clip forever while the controller plays through the playlist.
    setLoopMode(state.loopMode);
    // Older clients in the room do not send the layout at all; then leave ours alone.
    if (typeof state.stripOpen === 'boolean') setStripOpen(state.stripOpen);
    if (typeof state.notesOpen === 'boolean') setNotesOpen(state.notesOpen);
    setTransform((prev) => {
      const next = { zoom: state.zoom ?? 1, panX: state.panX ?? 0, panY: state.panY ?? 0 };
      // Same numbers must yield the same object, or the heartbeat re-renders the stage twice a second.
      return prev.zoom === next.zoom && prev.panX === next.panX && prev.panY === next.panY ? prev : next;
    });
  }, [sync.state, isController, soloMode, follow]);

  // Live status/comment changes from anyone in the room.
  useEffect(
    () =>
      sync.onVersionChanged((versionId, kind, payload) => {
        const data = payload as { version?: Version };
        if (data.version) {
          setItems((prev) =>
            prev.map((item) =>
              item.version.id === versionId ? { ...item, version: data.version as Version } : item,
            ),
          );
        }
        if (versionId === version?.id && (kind === 'comment' || kind === 'status' || kind === 'comment-deleted')) {
          void loadComments(versionId).catch(() => undefined);
        }
      }),
    [sync, version?.id, loadComments],
  );

  // Strokes drawn by other participants, shown live while they draw.
  const [remoteStrokes, setRemoteStrokes] = useState<Stroke[]>([]);
  useEffect(
    () =>
      sync.onDraw((event) => {
        const draw = event.draw as
          | { versionId?: string; frame?: number; stroke?: Stroke; clear?: boolean }
          | undefined;
        if (!draw) return;
        if (draw.clear) return setRemoteStrokes([]);
        // Only show a peer's live stroke if it was drawn on the frame we are looking at.
        if (draw.stroke && draw.versionId === version?.id && draw.frame === frameRef.current) {
          setRemoteStrokes((prev) => [...prev, draw.stroke as Stroke]);
        }
      }),
    [sync, version?.id],
  );
  // Live strokes are frame-scoped, so discard them whenever the frame or clip changes.
  useEffect(() => setRemoteStrokes([]), [version?.id, player.frame]);

  // -------------------------------------------------------------- navigation

  const jumpTo = useCallback(
    (index: number) => {
      if (lockedOut) return; // the host is presenting; followers do not change the clip
      flushDraftRef.current(); // save what was drawn on the clip being left, before it changes
      const landed = select(index);
      setTransform(NO_TRANSFORM);
      // A clip change always drops compare: a wipe or side-by-side is set up against one shot's
      // versions, and carrying it onto the next clip is never what the reviewer meant.
      setCompareMode('off');
      setCompareVersionId(null);
      player.seekFrame(0);
      pushState({
        itemIndex: landed.index,
        versionId: landed.versionId,
        mediaTime: 0,
        compareMode: 'off',
        compareVersionId: null,
        ...NO_TRANSFORM,
      });
    },
    [lockedOut, select, pushState, player.seekFrame],
  );

  const stepShot = useCallback((delta: number) => jumpTo(currentIndexRef.current + delta), [jumpTo, currentIndexRef]);

  // Switch which version of the current clip is shown; resets playback like a clip change.
  const chooseVersion = useCallback(
    (shotId: string, versionId: string) => {
      if (lockedOut) return; // presented-to followers stay on the host's version
      flushDraftRef.current(); // a drawing on the version being left is saved against it
      show(shotId, versionId);
      setTransform(NO_TRANSFORM);
      player.seekFrame(0);
      pushState({ versionId, mediaTime: 0, ...NO_TRANSFORM });
    },
    [lockedOut, show, pushState, player.seekFrame],
  );

  // Drag-reorder the clips (admin only). Reorder locally right away, persist the new order to
  // the playlist, and push it to the room so everyone follows live. On a rejected save (e.g. no
  // valid token) surface the error but keep the optimistic local order — a reload reconciles.
  const reorderClips = useCallback(
    (shotIds: string[]) => {
      const next = orderItemsByShots(items, shotIds);
      setItems(next);
      setManualOrder(true);
      pushState({ clipOrder: shotIds });
      void api
        .reorderPlaylist(playlistId, next.map((it) => it.itemId))
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    },
    [items, playlistId, pushState],
  );

  // Sequence mode (loop off): when a clip ends, roll straight on to the next shot and keep
  // playing. resumePlayRef carries the intent across the clip switch (a fresh <video> mounts).
  const resumePlayRef = useRef(false);
  useEffect(() => {
    const video = player.videoRef.current;
    if (!video || loopMode) return;
    const onEnded = (): void => {
      if (followerRef.current) return; // the controller advances the room, everyone else follows
      if (currentIndexRef.current >= groups.length - 1) return; // last clip: stop at the end
      resumePlayRef.current = true;
      stepShot(1);
    };
    video.addEventListener('ended', onEnded);
    return () => video.removeEventListener('ended', onEnded);
  }, [loopMode, stepShot, player.videoRef, version?.id, quality, groups.length, currentIndexRef]);

  // After a sequence auto-advance, start the freshly loaded clip with as little gap as possible.
  useEffect(() => {
    if (!resumePlayRef.current) return;
    const video = player.videoRef.current;
    if (!video) return;
    resumePlayRef.current = false;
    const start = (): void => {
      player.play();
      pushState({ playing: true, mediaTime: 0 });
    };
    if (video.readyState >= 2) start();
    else video.addEventListener('loadeddata', start, { once: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.id]);

  // Feeds the host's "Viewed by" tab. Fires for every participant, once per clip switch — a
  // failed record must never interrupt the review itself.
  useEffect(() => {
    if (!version?.id) return;
    void api.recordView(version.id, sessionId).catch(() => undefined);
  }, [version?.id, sessionId]);

  const canCompare = (currentGroup?.versions.length ?? 0) > 1;

  // Compare only makes sense with more than one version of the current clip; leaving a
  // single-version shot must not carry an active wipe over to it.
  useEffect(() => {
    if (!canCompare && compareMode !== 'off') {
      setCompareMode('off');
      pushState({ compareMode: 'off' });
    }
  }, [canCompare, compareMode, pushState]);

  /*
   * Depends on the one function it calls, not on the whole `player`.
   *
   * useFramePlayer returns a fresh object every render, so listing `player` here handed out a new
   * seekFrame every frame — and this one is a prop of the memoised notes list, which would then
   * never have been skipped. The individual callbacks inside the hook are stable.
   */
  const playerSeekFrame = player.seekFrame;
  const seekFrame = useCallback(
    (frame: number) => {
      if (lockedOut) return; // the playhead belongs to the presenter
      playerSeekFrame(frame);
      pushState({ mediaTime: (frame + 0.5) / fps, playing: false });
    },
    [lockedOut, playerSeekFrame, pushState, fps],
  );

  const togglePlay = useCallback(() => {
    if (lockedOut) return; // play/pause belongs to the presenter
    const next = !player.playing;
    player.togglePlay();
    pushState({ playing: next, mediaTime: player.currentTime() });
  }, [lockedOut, player, pushState]);

  const toggleLoopMode = useCallback(() => {
    const next = !loopMode;
    setLoopMode(next);
    pushState({ loopMode: next });
  }, [loopMode, pushState]);

  const changeTransform = useCallback(
    (t: { zoom: number; panX: number; panY: number }) => {
      setTransform(t);
      pushState({ zoom: t.zoom, panX: t.panX, panY: t.panY });
    },
    [pushState],
  );

  const changeCompareMode = useCallback(
    (mode: CompareMode) => {
      setCompareMode(mode);
      let nextCompareId = compareVersionId;
      if (mode !== 'off' && !compareOptions.some((v) => v.id === compareVersionId)) {
        // Entering compare with nothing valid picked yet: default to the next-smaller version —
        // the one you almost always want to check a new version against — rather than an empty
        // picker. Fall back to the highest other version when the current one is already lowest.
        const cur = version?.versionNumber ?? 0;
        const byNumberDesc = [...compareOptions].sort((a, b) => b.versionNumber - a.versionNumber);
        const pick = byNumberDesc.find((v) => v.versionNumber < cur) ?? byNumberDesc[0];
        nextCompareId = pick?.id ?? null;
        setCompareVersionId(nextCompareId);
      }
      pushState({ compareMode: mode, compareVersionId: nextCompareId });
    },
    [pushState, compareOptions, compareVersionId, version?.versionNumber],
  );

  const changeCompareVersion = useCallback(
    (id: string | null) => {
      setCompareVersionId(id);
      pushState({ compareVersionId: id });
    },
    [pushState],
  );

  const changeQuality = useCallback(
    (q: Quality) => {
      setQuality(q);
      pushState({ quality: q });
    },
    [pushState],
  );

  // Toggle solo. If a controller opts out, they must also release the room — otherwise the
  // room has a controller who quietly stops driving it, and nobody else can take over.
  const toggleSolo = useCallback(() => {
    setSoloMode((prev) => {
      const next = !prev;
      if (next && isController) sync.releaseControl();
      return next;
    });
  }, [isController, sync]);

  // The pen stands down while the host is presenting.
  const toggleDraw = useCallback(() => {
    if (lockedOut) return;
    draft.toggle();
  }, [lockedOut, draft.toggle]);

  // When the host takes over, pull this browser fully into line: drop out of any live draw mode
  // and rejoin the room if it had gone solo, so "control lies only with the host" actually holds.
  useEffect(() => {
    if (!lockedOut) return;
    if (draft.enabled) draft.setEnabled(false);
    if (soloMode) setSoloMode(false);
  }, [lockedOut, draft.enabled, draft.setEnabled, soloMode]);

  // ----------------------------------------------------------- review actions

  const applyStatus = useCallback(
    async (status: VersionStatus, comment?: string) => {
      if (!version) return;
      try {
        const res = await api.setStatus(version.id, {
          status,
          comment,
          frame: draft.frameFor(player.frame),
          author: displayName,
          annotation: draft.payloadFor(version, player.frame, drawBackgroundRef.current),
        });
        setItems((prev) =>
          prev.map((item) => (item.version.id === version.id ? { ...item, version: res.version } : item)),
        );
        draft.clear();
        await loadComments(version.id);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [version, draft, player.frame, displayName, loadComments],
  );

  // Approve/Reject act as toggles: pressing an active state clears it back to pending.
  const toggleApprove = useCallback(() => {
    void applyStatus(version?.status === 'approved' ? 'pending' : 'approved');
  }, [applyStatus, version?.status]);

  const toggleReject = useCallback(() => {
    if (version?.status === 'rejected') void applyStatus('pending');
    else setRejectOpen(true);
  }, [applyStatus, version?.status]);

  const addComment = useCallback(
    async (body: string, anchorToFrame: boolean) => {
      if (!version) return;
      await api.addComment(version.id, {
        body,
        frame: anchorToFrame ? player.frame : undefined,
        author: displayName,
        annotation: draft.payloadFor(version, player.frame, drawBackgroundRef.current),
      });
      draft.clear();
      await loadComments(version.id);
    },
    [version, draft, player.frame, displayName, loadComments],
  );

  const deleteComment = useCallback(
    (id: string) => {
      void api
        .deleteComment(id)
        .then(() => version && loadComments(version.id))
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    },
    [version, loadComments],
  );

  /*
   * Annotations saved on the frame currently displayed — and only while the clip is parked on
   * it. During playback they would strobe past for a single frame each, which reads as a
   * glitch rather than as a note; drawings are something you stop to look at.
   */
  const frameAnnotations = useMemo(
    () =>
      player.playing
        ? NO_STROKES
        : comments
            .flatMap((c) => c.annotations)
            .filter((a) => a.frame === player.frame)
            .flatMap((a) => a.strokes),
    [comments, player.frame, player.playing],
  );

  /*
   * The two stroke props the stage receives, kept referentially stable.
   *
   * `player.frame` changes on every displayed frame, so these recompute constantly during
   * playback — but while playing both collapse to the same empty constant, so what the stage
   * actually receives stops changing. That is the difference between the annotation layer
   * repainting its canvas (and rebuilding its ResizeObserver) once and doing it 25 times a
   * second on a clip that has nothing drawn on it at all.
   */
  const overlayStrokes = useMemo(
    () => (frameAnnotations.length === 0 && remoteStrokes.length === 0
      ? NO_STROKES
      : [...frameAnnotations, ...remoteStrokes]),
    [frameAnnotations, remoteStrokes],
  );

  const stageStrokes = useMemo(
    () => (player.playing ? NO_STROKES : draft.strokesOn(player.frame)),
    [player.playing, player.frame, draft.strokesOn],
  );

  const handleWipeChange = useCallback(
    (w: number) => {
      setWipe(w);
      pushState({ wipe: w });
    },
    [pushState],
  );

  // Reads the live frame and clip through refs: closing over them directly would hand the stage
  // a new callback on every frame, which is exactly what this is trying to avoid.
  const sendDraw = sync.sendDraw;
  const handleStrokeComplete = useCallback(
    (stroke: Stroke) => sendDraw({ versionId: versionIdRef.current, frame: frameRef.current, stroke }),
    [sendDraw],
  );

  // ------------------------------------------------------------- keyboard

  useReviewKeys({
    frame: player.frame,
    frameCount,
    seek: seekFrame,
    togglePlay,
    stepShot,
    changeRate: (factor) => player.setRate(Math.min(4, Math.max(0.25, player.rate * factor))),
    toggleApprove,
    toggleReject,
    toggleDraw,
    toggleWipe: () => changeCompareMode(compareMode === 'wipe' ? 'off' : 'wipe'),
    toggleFocus,
    undo: draft.undo,
    enabled: !rejectOpen,
  });

  // ------------------------------------------------------------------ render

  return (
    <div className="review">
      <ReviewHeader
        playlistName={playlistName}
        group={currentGroup}
        version={version}
        onChooseVersion={chooseVersion}
        quality={quality}
        onQuality={changeQuality}
        sync={sync}
        soloMode={soloMode}
        onToggleSolo={toggleSolo}
        canTakeControl={canReorder}
      />

      {error ? (
        <div className="banner banner--error" onClick={() => setError(null)}>
          {error}
        </div>
      ) : null}

      <div className={`review__main${notesOpen ? '' : ' review__main--notes-collapsed'}`}>
        <div className="review__stage-col">
          <PlayerStage
            primary={version}
            next={nextVersion}
            compare={compareVersion}
            compareMode={compareMode}
            wipe={wipe}
            onWipeChange={handleWipeChange}
            zoom={transform.zoom}
            panX={transform.panX}
            panY={transform.panY}
            onTransformChange={changeTransform}
            quality={quality}
            primaryRef={player.videoRef}
            compareRef={compareRef}
            muted={muted}
            loop={loopMode}
            drawEnabled={draft.enabled && !lockedOut}
            strokes={stageStrokes}
            onStrokesChange={draft.replace}
            overlayStrokes={overlayStrokes}
            tool={draft.tool}
            color={draft.color}
            lineWidth={draft.lineWidth}
            onStrokeComplete={handleStrokeComplete}
          />

          <Scrubber
            frame={player.frame}
            frameCount={frameCount}
            startFrame={startFrame}
            buffered={player.buffered}
            comments={comments}
            onSeek={seekFrame}
          />

          {draft.enabled ? (
            <DrawToolbar
              draft={draft}
              onDiscard={() => {
                draft.clear();
                sync.sendDraw({ clear: true });
              }}
            />
          ) : null}

          <ReviewControls
            version={version}
            frame={player.frame}
            frameCount={frameCount}
            startFrame={startFrame}
            fps={fps}
            playing={player.playing}
            onTogglePlay={togglePlay}
            onStepShot={stepShot}
            loopMode={loopMode}
            onToggleLoopMode={toggleLoopMode}
            canCompare={canCompare}
            compareMode={compareMode}
            onCompareMode={changeCompareMode}
            compareOptions={compareOptions}
            compareVersionId={compareVersionId}
            onCompareVersion={changeCompareVersion}
            drawEnabled={draft.enabled}
            onToggleDraw={toggleDraw}
            drawDisabled={lockedOut}
            muted={muted}
            onToggleMuted={() => setMuted((m) => !m)}
            canDownload={canDownload}
            onApprove={toggleApprove}
            onReject={toggleReject}
          />

          <Timeline
            items={stripItems}
            currentIndex={currentIndex}
            onJump={jumpTo}
            reorderable={canReorder}
            onReorder={reorderClips}
            open={stripOpen}
            onToggle={toggleStrip}
          />
        </div>

        <CommentPanel
          version={version}
          comments={comments}
          currentFrame={player.frame}
          fps={fps}
          startFrame={startFrame}
          onJumpToFrame={seekFrame}
          onAddComment={addComment}
          onDeleteComment={deleteComment}
          open={notesOpen}
          onToggle={toggleNotes}
          canSeeViewers={canReorder}
          sessionId={sessionId}
        />
      </div>

      <RejectDialog
        open={rejectOpen}
        versionLabel={version?.label ?? ''}
        frame={player.frame}
        startFrame={startFrame}
        hasDrawing={draft.strokes.length > 0}
        onCancel={() => setRejectOpen(false)}
        onConfirm={async (comment) => {
          await applyStatus('rejected', comment);
          setRejectOpen(false);
        }}
      />
    </div>
  );
}
